import type { ReactNode, CSSProperties } from 'react';

interface GradientBackgroundProps {
  children: ReactNode;
  style?: CSSProperties;
}

/**
 * Full-screen animated aurora / liquid-glass background.
 * Renders four slow-floating glowing orbs over a deep space gradient.
 */
export default function GradientBackground({ children, style }: GradientBackgroundProps) {
  return (
    <div style={{ ...containerStyle, ...style }}>
      {/* Deep space base gradient */}
      <div style={baseGradientStyle} />

      {/* Floating aurora orbs */}
      <div style={orb1Style} />
      <div style={orb2Style} />
      <div style={orb3Style} />
      <div style={orb4Style} />

      {/* Grain overlay for texture */}
      <div style={grainStyle} />

      {/* Content layer */}
      <div style={contentStyle} className="rounded-none">{children}</div>
    </div>
  );
}

/* ---- Styles ---- */

const containerStyle: CSSProperties = {
  position: 'relative',
  minHeight: '100vh',
  width: '100%',
  overflow: 'hidden',
  backgroundColor: '#07021a',
};

const baseGradientStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  background:
    'radial-gradient(ellipse 80% 60% at 50% 0%, #1a0640 0%, #07021a 60%), ' +
    'radial-gradient(ellipse 60% 50% at 100% 100%, #030e2e 0%, transparent 70%)',
  zIndex: 0,
};

const orb1Style: CSSProperties = {
  position: 'absolute',
  top: '-10%',
  left: '-5%',
  width: '55vw',
  height: '55vw',
  maxWidth: '700px',
  maxHeight: '700px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(139, 92, 246, 0.35) 0%, rgba(109, 40, 217, 0.12) 50%, transparent 70%)',
  filter: 'blur(40px)',
  animation: 'float-orb-1 18s ease-in-out infinite',
  zIndex: 1,
  pointerEvents: 'none',
};

const orb2Style: CSSProperties = {
  position: 'absolute',
  bottom: '-15%',
  right: '-8%',
  width: '50vw',
  height: '50vw',
  maxWidth: '640px',
  maxHeight: '640px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(6, 182, 212, 0.28) 0%, rgba(8, 145, 178, 0.08) 55%, transparent 70%)',
  filter: 'blur(45px)',
  animation: 'float-orb-2 22s ease-in-out infinite',
  zIndex: 1,
  pointerEvents: 'none',
};

const orb3Style: CSSProperties = {
  position: 'absolute',
  top: '30%',
  right: '-10%',
  width: '40vw',
  height: '40vw',
  maxWidth: '520px',
  maxHeight: '520px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(236, 72, 153, 0.22) 0%, rgba(219, 39, 119, 0.06) 55%, transparent 70%)',
  filter: 'blur(50px)',
  animation: 'float-orb-3 16s ease-in-out infinite',
  zIndex: 1,
  pointerEvents: 'none',
};

const orb4Style: CSSProperties = {
  position: 'absolute',
  bottom: '5%',
  left: '-12%',
  width: '45vw',
  height: '45vw',
  maxWidth: '600px',
  maxHeight: '600px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.06) 55%, transparent 70%)',
  filter: 'blur(55px)',
  animation: 'float-orb-4 20s ease-in-out infinite',
  zIndex: 1,
  pointerEvents: 'none',
};

const grainStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.035'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'repeat',
  backgroundSize: '200px 200px',
  opacity: 0.5,
  zIndex: 2,
  pointerEvents: 'none',
};

const contentStyle: CSSProperties = {
  position: 'relative',
  zIndex: 3,
  minHeight: '100vh',
};
